import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { SyncAltIcon, UserTagIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useApiFetch } from '../../../api';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import {
  type BackupAttributionQueueFilters,
  useBackupAttributionQueue,
} from '../../../hooks/useBackupAttributionQueue';
import { type BackupVmMappingFilters, useBackupVmMappings } from '../../../hooks/useBackupVmMappings';
import type { BackupAttributionCandidate, BackupVmMapping } from '../../../types/models/BackupVmMapping';
import type { ApiResponseBackupVmMappingSingle } from '../../../types/api/ApiResponseBackupVmMapping';
import BackupAttributionQueueTab, { type DirectoryOption } from './BackupAttributionQueueTab';
import BackupOverridesTab, { type ClientFilterOption } from './BackupOverridesTab';
import BackupAttributionModal from './BackupAttributionModal';
import BackupAttributionRemoveModal from './BackupAttributionRemoveModal';
import { attributionSavedNotification } from './attributionMessages';

const TAB_QUEUE = 'queue';
const TAB_OVERRIDES = 'overrides';

const directoryLabelOf = (mapping: BackupVmMapping): string =>
  mapping.backup_target !== null && mapping.customer_directory !== null
    ? `${mapping.backup_target} / ${mapping.customer_directory}`
    : `Directory #${mapping.backup_mapping_id}`;

/**
 * Backup Attribution — the Ancalagon-staff-only screen that decides which customer a backed-up
 * virtual machine belongs to.
 *
 * A level-2 `<customer>` directory attributes a whole backup tree to one client, which is only
 * correct when that tree is dedicated. `/opt/backup/<target>/ancalagon/` is not: it means "these
 * are Ancalagon's HYPERVISORS", and the VMs inside them belong to customers — over half of the
 * estate's backed-up VMs sit under one such shared directory. Until a staff member says otherwise
 * every one of them is attributed to the house client and invisible to its real owner. This page
 * is where that is fixed, one VM at a time.
 *
 * **There is deliberately no bulk apply anywhere in this feature.** NetBox is wrong for exactly
 * the VMs this screen exists to re-attribute (the Manager Agent stamps every discovered VM with
 * its *hypervisor's* owner), and a VM name is not an identity — a tenant can rename a VM to any
 * string. So NetBox suggests, per row, with how it got there, and a human decides.
 *
 * Gating is enforced in two independent places (defense in depth):
 *   1. AppLayout hides the whole "Admin" nav group unless `user.is_ancalagon_staff`.
 *   2. This page guards itself: a non-staff user who types the URL gets <NotFound /> — the same
 *      response as a non-existent route, so the admin area's existence is never leaked. The
 *      backend `/admin` routes enforce this authoritatively; the flag is a UI hint, never the
 *      security boundary.
 */
function BackupAttributions() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [activeTab, setActiveTab] = useState<string>(TAB_QUEUE);

  const [queueFilters, setQueueFilters] = useState<BackupAttributionQueueFilters>({
    backupMappingId: null,
    all: false,
    includeMissing: false,
  });
  const [overrideFilters, setOverrideFilters] = useState<BackupVmMappingFilters>({
    backupMappingId: null,
    clientID: null,
    orphansOnly: false,
  });

  // `isStaff` gates both fetches so a non-staff visitor (shown <NotFound /> below) never fires the
  // backend-rejected admin calls.
  const {
    candidates,
    netboxAvailable,
    netboxReason,
    truncated,
    loading: queueLoading,
    error: queueError,
    refetch: refetchQueue,
    removeCandidate,
  } = useBackupAttributionQueue(isStaff, queueFilters);

  // The overrides list is fetched at PAGE level rather than inside its tab: its `orphan_count`
  // feeds the tab badge, which has to be visible before the tab is ever opened. The <Tabs> below
  // still uses mountOnEnter so the table itself is only built when the tab is first shown.
  const {
    mappings,
    orphanCount,
    loading: overridesLoading,
    error: overridesError,
    refetch: refetchOverrides,
    applyMapping,
    removeMapping,
  } = useBackupVmMappings(isStaff, overrideFilters);

  const [modalCandidate, setModalCandidate] = useState<BackupAttributionCandidate | null>(null);
  const [modalMapping, setModalMapping] = useState<BackupVmMapping | null>(null);
  const [removeTarget, setRemoveTarget] = useState<BackupVmMapping | null>(null);
  const [attributingId, setAttributingId] = useState<number | null>(null);

  // A mutation on one tab changes what the other tab means (attributing settles a queue row and
  // creates an override; removing an override can put a VM back in the queue). Both lists are
  // patched in place for an instant, flash-free update, and the *other* tab is marked stale so it
  // refetches the authoritative rows the next time it is shown.
  const [queueStale, setQueueStale] = useState(false);
  const [overridesStale, setOverridesStale] = useState(false);

  // Filter options are accumulated rather than derived: filtering server-side by directory (or
  // client) would otherwise shrink the option list to the single value already selected, leaving
  // no way back.
  const [directoryLabels, setDirectoryLabels] = useState<Map<number, string>>(new Map());
  const [clientNames, setClientNames] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    setDirectoryLabels((prev) => {
      const next = new Map(prev);
      let changed = false;
      candidates.forEach((candidate) => {
        if (!next.has(candidate.backup_mapping_id)) {
          // Candidates carry the id but not the directory names; the overrides list fills those in
          // when it knows them.
          next.set(candidate.backup_mapping_id, `Directory #${candidate.backup_mapping_id}`);
          changed = true;
        }
      });
      mappings.forEach((mapping) => {
        const label = directoryLabelOf(mapping);
        if (next.get(mapping.backup_mapping_id) !== label) {
          next.set(mapping.backup_mapping_id, label);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [candidates, mappings]);

  useEffect(() => {
    setClientNames((prev) => {
      const next = new Map(prev);
      let changed = false;
      mappings.forEach((mapping) => {
        const name = mapping.client_name ?? `Client #${mapping.clientID}`;
        if (next.get(mapping.clientID) !== name) {
          next.set(mapping.clientID, name);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [mappings]);

  const directoryOptions = useMemo<DirectoryOption[]>(
    () =>
      Array.from(directoryLabels.entries())
        .map(([id, label]) => ({ id, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [directoryLabels],
  );

  const clientOptions = useMemo<ClientFilterOption[]>(
    () =>
      Array.from(clientNames.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [clientNames],
  );

  const directoryLabelFor = useCallback(
    (backupMappingId: number): string => directoryLabels.get(backupMappingId) ?? `Directory #${backupMappingId}`,
    [directoryLabels],
  );

  const handleSaved = useCallback(
    (saved: BackupVmMapping, previous: BackupVmMapping | null, settledCandidateId: number | null) => {
      applyMapping(saved, previous);
      if (settledCandidateId !== null) {
        // A settled VM must not sit in the review queue inviting a second, now-invalid decision.
        removeCandidate(settledCandidateId);
      }
      setOverridesStale(true);
      setQueueStale(true);
    },
    [applyMapping, removeCandidate],
  );

  const handleRemoved = useCallback(
    (removed: BackupVmMapping) => {
      removeMapping(removed);
      // The VM reverts to its directory's client, which may well put it back in the queue.
      setQueueStale(true);
    },
    [removeMapping],
  );

  /**
   * Accept a suggestion for ONE row: one click, one POST, no confirmation — it is reversible from
   * the Overrides tab. This is the only place a suggestion is ever applied, and it is always
   * driven by a staff member clicking that row's own button.
   */
  const handleQuickAttribute = useCallback(
    async (candidate: BackupAttributionCandidate) => {
      if (candidate.suggestion.clientID === null) {
        return;
      }
      setAttributingId(candidate.server_backup_id);
      try {
        const response = await apiFetch('/admin/backup-vm-mappings', {
          method: 'POST',
          body: JSON.stringify({
            backup_mapping_id: candidate.backup_mapping_id,
            hypervisor: candidate.hypervisor,
            vm_name: candidate.vm_name,
            clientID: candidate.suggestion.clientID,
          }),
        });
        // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
        // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
        const body = (await response.json().catch(() => null)) as ApiResponseBackupVmMappingSingle | null;

        if (body?.status === 'success' && body.data?.backup_vm_mapping) {
          const saved = body.data.backup_vm_mapping;
          const restamped = typeof body.data.subject_restamped === 'number' ? body.data.subject_restamped : 0;
          handleSaved(saved, null, candidate.server_backup_id);
          addNotification(attributionSavedNotification(saved, restamped, false));
          setDrawerOpen(true);
          return;
        }

        const fieldError = Object.values(body?.data ?? {}).find((value) => typeof value === 'string');
        addNotification({
          title: 'Could not attribute the virtual machine',
          srTitle: 'Error alert',
          description:
            (typeof fieldError === 'string' ? fieldError : null) ??
            body?.message ??
            'The attribution could not be saved. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not attribute the virtual machine',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setAttributingId(null);
      }
    },
    [apiFetch, handleSaved, addNotification, setDrawerOpen],
  );

  const handleTabSelect = (_event: React.MouseEvent<HTMLElement, MouseEvent>, tabKey: string | number) => {
    const key = String(tabKey);
    setActiveTab(key);
    if (key === TAB_OVERRIDES && overridesStale) {
      setOverridesStale(false);
      void refetchOverrides();
    }
    if (key === TAB_QUEUE && queueStale) {
      setQueueStale(false);
      void refetchQueue();
    }
  };

  const canEditOverride = useCallback(
    (candidate: BackupAttributionCandidate) =>
      candidate.override_id !== null && mappings.some((mapping) => mapping.id === candidate.override_id),
    [mappings],
  );

  const handleEditOverrideFromQueue = useCallback(
    (candidate: BackupAttributionCandidate) => {
      const mapping = mappings.find((row) => row.id === candidate.override_id);
      if (mapping !== undefined) {
        setModalMapping(mapping);
      }
    },
    [mappings],
  );

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  // Ancalagon-staff-only. Treat anything other than an explicit `true` as "no access" and render
  // the 404 page so the route reveals nothing about the admin area's existence.
  if (!isStaff) {
    return <NotFound />;
  }

  const isRefreshing = activeTab === TAB_QUEUE ? queueLoading : overridesLoading;

  const modalDirectoryLabel =
    modalMapping !== null
      ? directoryLabelOf(modalMapping)
      : modalCandidate !== null
        ? directoryLabelFor(modalCandidate.backup_mapping_id)
        : '—';

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Backup Attribution</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsFlexStart' }}>
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <UserTagIcon />
              </Icon>
              Backup Attribution
            </Content>
            <Content component="p">Decide which customer each backed-up virtual machine belongs to.</Content>
          </FlexItem>
          <FlexItem>
            {/* Kept mounted while refreshing (isLoading) rather than vanishing into the tab's
                loading spinner. */}
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              isLoading={isRefreshing}
              spinnerAriaValueText="Refreshing"
              onClick={() => {
                if (isRefreshing) {
                  return;
                }
                if (activeTab === TAB_QUEUE) {
                  setQueueStale(false);
                  void refetchQueue();
                } else {
                  setOverridesStale(false);
                  void refetchOverrides();
                }
              }}
            >
              Refresh
            </Button>
          </FlexItem>
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs activeKey={activeTab} onSelect={handleTabSelect} aria-label="Backup attribution tabs" mountOnEnter>
          <Tab eventKey={TAB_QUEUE} title={<TabTitleText>Review queue</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <BackupAttributionQueueTab
                candidates={candidates}
                loading={queueLoading}
                error={queueError}
                netboxAvailable={netboxAvailable}
                netboxReason={netboxReason}
                truncated={truncated}
                filters={queueFilters}
                onFiltersChange={setQueueFilters}
                directoryOptions={directoryOptions}
                onRetry={() => void refetchQueue()}
                onQuickAttribute={(candidate) => void handleQuickAttribute(candidate)}
                attributingId={attributingId}
                onAttributeManually={setModalCandidate}
                onEditOverride={handleEditOverrideFromQueue}
                canEditOverride={canEditOverride}
              />
            </div>
          </Tab>
          <Tab
            eventKey={TAB_OVERRIDES}
            title={
              <>
                <TabTitleText>Overrides</TabTitleText>
                {orphanCount > 0 && (
                  <>
                    {' '}
                    <Badge isRead screenReaderText="attributions matching no reported backup">
                      {orphanCount}
                    </Badge>
                  </>
                )}
              </>
            }
          >
            <div className="pf-v6-u-pt-md">
              <BackupOverridesTab
                mappings={mappings}
                loading={overridesLoading}
                error={overridesError}
                filters={overrideFilters}
                onFiltersChange={setOverrideFilters}
                clientOptions={clientOptions}
                onRetry={() => void refetchOverrides()}
                onEdit={setModalMapping}
                onRemove={setRemoveTarget}
              />
            </div>
          </Tab>
        </Tabs>
      </PageSection>

      {/* Mounted only while open, so each open starts from a fresh mount (clean form state) and the
          modal's /clients fetch — which FAILS during an impersonation session — never runs on page
          load. */}
      {(modalCandidate !== null || modalMapping !== null) && (
        <BackupAttributionModal
          candidate={modalCandidate}
          mapping={modalMapping}
          directoryLabel={modalDirectoryLabel}
          onClose={() => {
            setModalCandidate(null);
            setModalMapping(null);
          }}
          onSaved={(saved, previous) => handleSaved(saved, previous, modalCandidate?.server_backup_id ?? null)}
        />
      )}
      {removeTarget !== null && (
        <BackupAttributionRemoveModal
          mapping={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onRemoved={(removed) => handleRemoved(removed)}
        />
      )}
    </>
  );
}

export { BackupAttributions };
